import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';

const ImageContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
  background-color: #111;
`;

// Use transient props with $ prefix to prevent them from being passed to the DOM
const StyledImage = styled.img`
  width: 100%;
  height: 100%;
  transition: opacity 0.3s ease, transform 0.3s ease;
  opacity: ${props => props.$isLoaded ? 1 : 0};
  transform: scale(${props => props.$isLoaded ? 1 : 0.95});
  object-fit: ${props => props.$objectFit || 'cover'};

  ${props => props.$hoverEffect && `
    &:hover {
      transform: scale(1.05);
    }
  `}
`;

const Placeholder = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: #111;
  display: flex;
  justify-content: center;
  align-items: center;
  opacity: ${props => props.$isLoaded ? 0 : 1};
  transition: opacity 0.3s ease;
`;

const LoadingSpinner = styled.div`
  width: 30px;
  height: 30px;
  border: 3px solid rgba(255, 255, 255, 0.1);
  border-top-color: #2c7d46;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

function LazyImage({ src, alt, objectFit = 'cover', hoverEffect = false, onClick, className }) {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const [currentSrc, setCurrentSrc] = useState(src);
  const [fallbackAttempts, setFallbackAttempts] = useState(0);
  const maxFallbackAttempts = 3; // Maximum number of fallback attempts
  const imgRef = useRef(null);

  // Set up Intersection Observer to detect when the image is in view
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      { rootMargin: '100px' } // Start loading when image is 100px from viewport
    );

    if (imgRef.current) {
      observer.observe(imgRef.current);
    }

    return () => {
      if (imgRef.current) {
        observer.disconnect();
      }
    };
  }, []);

  // Update currentSrc when src prop changes
  useEffect(() => {
    setCurrentSrc(src);
    setFallbackAttempts(0);
    setIsLoaded(false);
  }, [src]);

  // Handle image load
  const handleImageLoad = () => {
    setIsLoaded(true);
  };

  // Handle image error with simplified approach - no custom collection concept
  const handleImageError = () => {
    console.error(`[LazyImage] ERROR: Failed to load image: ${currentSrc} (Attempt ${fallbackAttempts + 1}/${maxFallbackAttempts})`);

    // Log additional debug info
    if (alt) {
      console.log(`[LazyImage] Alt text: "${alt}"`);
    }

    // If we haven't exceeded the maximum number of fallback attempts
    if (fallbackAttempts < maxFallbackAttempts) {
      // Increment the fallback attempt counter
      const nextAttempt = fallbackAttempts + 1;
      setFallbackAttempts(nextAttempt);

      // First attempt: try a direct path in the images folder
      if (nextAttempt === 1) {
        try {
          // Extract the filename from the path
          const parts = currentSrc.split('/');
          const filename = parts[parts.length - 1];

          // Try a direct path to the image
          const directPath = `/images/${filename}`;
          console.log(`[LazyImage] Trying direct image path: ${directPath}`);
          setCurrentSrc(directPath);
          return;
        } catch (error) {
          console.error('[LazyImage] Error creating alternative path:', error);
        }
      }
      // Second attempt: try with a different naming pattern
      else if (nextAttempt === 2) {
        try {
          const parts = currentSrc.split('/');
          const filename = parts[parts.length - 1];

          // Try alternative naming patterns
          if (/\d+\.jpg$/.test(filename)) {
            // If it's a numbered pattern, try with _large suffix
            const altPath = filename.replace(/(\d+)\.jpg$/, '$1_large.jpg');
            const newPath = `/images/collections/${parts[parts.length - 2]}/${altPath}`;
            console.log(`[LazyImage] Trying with _large suffix: ${newPath}`);
            setCurrentSrc(newPath);
            return;
          }

          // If that doesn't work, try the root path
          const rootPath = `/${filename}`;
          console.log(`[LazyImage] Trying root path: ${rootPath}`);
          setCurrentSrc(rootPath);
          return;
        } catch (error) {
          console.error('[LazyImage] Error creating second fallback path:', error);
        }
      }
      // Final attempt: use placeholder
      else if (nextAttempt >= maxFallbackAttempts - 1 && !currentSrc.includes('/placeholder.jpg')) {
        console.log('[LazyImage] Using placeholder image as final fallback');
        setCurrentSrc('/images/placeholder.jpg');
        return;
      }
    }

    // If we've exhausted all fallbacks, just mark as loaded to remove the spinner
    console.log('All fallback attempts exhausted, showing empty state');
    setIsLoaded(true);
  };

  return (
    <ImageContainer className={className} ref={imgRef} onClick={onClick}>
      {isInView && (
        <StyledImage
          src={currentSrc}
          alt={alt}
          onLoad={handleImageLoad}
          onError={handleImageError}
          $objectFit={objectFit}
          loading="lazy" // Use browser's native lazy loading
          $isLoaded={isLoaded}
          $hoverEffect={hoverEffect}
        />
      )}
      <Placeholder $isLoaded={isLoaded}>
        <LoadingSpinner />
      </Placeholder>
    </ImageContainer>
  );
}

export default LazyImage;